import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import {
  BillsTableWrapper,
  BillsTableEl,
  BillsTableHeader,
  BillsTableRow,
  BillsTableData,
  StatusBadgeBills,
} from '../styles/BillsTableStyles';
import { FormRow, Label, SubmitButton } from '../styles/ComponentStyles';
import { BillNumberInput, BillDateInput } from '../styles/BillsFormStyles';
import { gainLossColor, formatMoney, formatPct } from '../utils/investmentMath';
import { sortByDate } from '../utils/sortByDate';

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({ value: '', date: today(), remarks: '' });

const textInputStyle = {
  width: '100%',
  maxWidth: 400,
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const linkBtnStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
});

// User-level list of aggregated portfolio values: "everything I hold was worth
// X on this date". Each row is compared against everything contributed on or
// before that date (contributed_as_of), so old snapshots stay honest.
const PortfolioValuesModal = ({ onClose, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const setFlash = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const list = await apiService.getPortfolioValues();
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (pv) => {
    setEditingId(pv.id);
    setPendingDeleteId(null);
    setForm({
      value: pv.value ?? '',
      date: pv.date || today(),
      remarks: pv.remarks ?? '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.value === '' || !form.date) {
      setFlash('Please fill value and date.');
      return;
    }
    const payload = {
      value: parseFloat(form.value),
      date: form.date,
      remarks: form.remarks || null,
    };
    try {
      if (editingId) {
        await apiService.updatePortfolioValue(editingId, payload);
        setFlash('Portfolio value updated.');
      } else {
        await apiService.createPortfolioValue(payload);
        setFlash('Portfolio value added.');
      }
      resetForm();
      await fetchItems();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to save portfolio value.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.deletePortfolioValue(id);
      setFlash('Portfolio value deleted.');
      setPendingDeleteId(null);
      if (editingId === id) resetForm();
      await fetchItems();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  const toggleSortDir = () => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const sortedItems = sortByDate(items, 'date', sortDir);

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860 }}>
        <ModalHeader>
          <ModalTitle>Portfolio Values</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {flashMessage && <FlashMessage message={flashMessage} />}

        <p style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: 0 }}>
          Record what everything you hold is worth on a date. Each entry is compared
          against everything you contributed on or before that date.
        </p>

        <form onSubmit={handleSubmit}>
          <h4 style={{ marginBottom: 12 }}>
            {editingId ? 'Edit portfolio value' : 'Add portfolio value'}
          </h4>
          <FormRow>
            <Label>Date</Label>
            <BillDateInput name="date" value={form.date} onChange={handleChange} required />
          </FormRow>
          <FormRow>
            <Label>Value</Label>
            <BillNumberInput
              name="value"
              min="0"
              value={form.value}
              onChange={handleChange}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Remarks (optional)</Label>
            <input
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              maxLength={500}
              style={textInputStyle}
            />
          </FormRow>
          <SubmitButton type="submit">
            {editingId ? 'Update Portfolio Value' : 'Add Portfolio Value'}
          </SubmitButton>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              style={{
                marginLeft: 12,
                padding: '8px 16px',
                background: '#555',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </form>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Recorded portfolio values</h4>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <BillsTableWrapper style={{ marginTop: 8 }}>
            <BillsTableEl style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <BillsTableHeader
                    onClick={toggleSortDir}
                    title="Sort by date"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Date {sortDir === 'desc' ? '▼' : '▲'}
                  </BillsTableHeader>
                  <BillsTableHeader>Value</BillsTableHeader>
                  <BillsTableHeader>Contributed as of</BillsTableHeader>
                  <BillsTableHeader>Gain / Loss</BillsTableHeader>
                  <BillsTableHeader>Remarks</BillsTableHeader>
                  <BillsTableHeader>Actions</BillsTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 16, color: '#888' }}>
                      No portfolio values yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((pv) => (
                    <BillsTableRow key={pv.id || pv.date + pv.value}>
                      <BillsTableData>{pv.date}</BillsTableData>
                      <BillsTableData>{formatMoney(pv.value)}</BillsTableData>
                      <BillsTableData>{formatMoney(pv.contributed_as_of)}</BillsTableData>
                      <BillsTableData>
                        {pv.gain_loss != null ? (
                          <StatusBadgeBills $color={gainLossColor(pv.gain_loss)}>
                            {pv.gain_loss >= 0 ? '+' : ''}
                            {formatMoney(pv.gain_loss)}
                            {pv.gain_loss_pct != null ? ` (${formatPct(pv.gain_loss_pct)})` : ''}
                          </StatusBadgeBills>
                        ) : (
                          <StatusBadgeBills $color={gainLossColor(null)}>
                            NO CONTRIBUTIONS
                          </StatusBadgeBills>
                        )}
                      </BillsTableData>
                      <BillsTableData>{pv.remarks || '—'}</BillsTableData>
                      <BillsTableData>
                        {pendingDeleteId === pv.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleDelete(pv.id)}
                              style={linkBtnStyle('#f44336')}
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDeleteId(null)}
                              style={linkBtnStyle('#bbb')}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEdit(pv)}
                              title="Edit"
                              style={linkBtnStyle('#4caf50')}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDeleteId(pv.id)}
                              style={linkBtnStyle('#f44336')}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </BillsTableData>
                    </BillsTableRow>
                  ))
                )}
              </tbody>
            </BillsTableEl>
          </BillsTableWrapper>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: '8px 16px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default PortfolioValuesModal;
